import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';

import useFetchHook from '@giro-pdv/hooks/useFetch.hook';

import getApiTerminalsRegistrationsIdOtpService from '@giro/shared-services/pdv/getApiTerminalsRegistrationsIdOtp.service';
import postApiTerminalsRegistrationsIdOtpService from '@giro/shared-services/pdv/postApiTerminalsRegistrationsIdOtp.service';

import { buildQrPayload, OtpReveal } from '../terminals.types';

const formatCountdown = (expiresAt?: string | null) => {
  if (!expiresAt) return null;
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(diffMs)) return null;
  if (diffMs <= 0) return 'expirado';
  const totalSec = Math.floor(diffMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}h ${pad(m)}m` : `${pad(m)}:${pad(s)}`;
};

const QrContent = ({
  registration,
  initialReveal,
  reauth,
  onClose,
}: {
  registration: { id: any };
  initialReveal?: OtpReveal | null;
  reauth?: boolean;
  onClose: () => void;
}) => {
  const [reveal, setReveal] = React.useState<OtpReveal | null>(
    initialReveal || null
  );
  const [, setTick] = React.useState(0);

  // Active terminals (reauth) have no stored OTP to reveal — mint a fresh one on
  // open via regenerate. First-time accreditation reveals the existing OTP.
  const otpFetch = useFetchHook(
    () => getApiTerminalsRegistrationsIdOtpService(registration.id),
    { requestOnMount: !initialReveal && !reauth }
  );

  // In reauth mode, flag the regenerate so the next provider-accreditation reinstalls the
  // provider terminal even if it is already active.
  const regenFetch = useFetchHook(
    () =>
      postApiTerminalsRegistrationsIdOtpService(
        registration.id,
        reauth ? { reauth: true } : {}
      ),
    { requestOnMount: Boolean(reauth) && !initialReveal }
  );

  React.useEffect(() => {
    if (otpFetch.data) setReveal(otpFetch.data);
  }, [otpFetch.data]);

  React.useEffect(() => {
    if (regenFetch.data) setReveal(regenFetch.data);
  }, [regenFetch.data]);

  // Tick every second so the expiry countdown updates.
  React.useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const handleRegenerate = async () => {
    setReveal(null);
    const [success, data] = await regenFetch.handleFetch();
    if (success && data) setReveal(data);
  };

  const loading = (otpFetch.loading || regenFetch.loading) && !reveal;
  const countdown = formatCountdown(reveal?.otp_expires_at);

  return (
    <>
      <DialogTitle>
        {reauth ? 'Reautenticar dispositivo' : 'Credenciar dispositivo'}
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          py={1}
        >
          <Typography variant="trasso_body_small" color="trasso.purple.60">
            {reauth
              ? 'Escaneie o QR Code no aplicativo do dispositivo e conclua a nova ativação antes de voltar a receber pagamentos.'
              : 'Escaneie o QR Code no aplicativo do dispositivo para concluir o credenciamento.'}
          </Typography>

          {loading && <CircularProgress />}

          {!loading && reveal?.otp && (
            <>
              <Box p={2} bgcolor="white" borderRadius="8px">
                <QRCodeCanvas
                  id="terminal-accreditation-qr"
                  value={buildQrPayload({
                    id: registration.id,
                    otp: reveal.otp,
                  })}
                  size={200}
                />
              </Box>

              <Box textAlign="center">
                <Typography
                  component="div"
                  variant="trasso_heading_small"
                  color="trasso.purple.100"
                >
                  Código: {reveal.otp}
                </Typography>
                {countdown && (
                  <Typography
                    component="div"
                    variant="trasso_body_extra_small"
                    color={
                      countdown === 'expirado' ? 'error' : 'trasso.purple.60'
                    }
                  >
                    {countdown === 'expirado'
                      ? 'Código expirado — gere um novo.'
                      : `Expira em ${countdown}`}
                  </Typography>
                )}
              </Box>
            </>
          )}

          {!loading && !reveal?.otp && (
            <Typography variant="trasso_body_small" color="error">
              Não foi possível obter o código deste dispositivo.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleRegenerate}
          disabled={regenFetch.loading}
          startIcon={
            regenFetch.loading ? <CircularProgress size={16} /> : undefined
          }
        >
          Gerar novo código
        </Button>
        <Button variant="contained" onClick={onClose}>
          Fechar
        </Button>
      </DialogActions>
    </>
  );
};

const DialogShowQrComponent = ({
  open,
  registration,
  initialReveal,
  reauth,
  onClose,
}: {
  open: boolean;
  registration: { id: any } | null;
  initialReveal?: OtpReveal | null;
  reauth?: boolean;
  onClose: () => void;
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {open && registration && (
        <QrContent
          registration={registration}
          initialReveal={initialReveal}
          reauth={reauth}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
};

export default DialogShowQrComponent;
